import React, { PropsWithChildren, useState } from 'react'
import {
  Categories,
  FilterFileType,
  Roles,
} from '../../utils/searchAndFilterEnums'
import { ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW } from '@utils/constants'

export interface BusinessContextType {
  search: string
  updateSearch: (search: string) => void
  role: Roles
  updateRole: (role: Roles) => void
  type: FilterFileType
  updateType: (type: FilterFileType) => void
  category: Categories
  updateCategory: (category: Categories) => void
  resetContextToDefaults: () => void
  updatePage: () => void
  resetPage: () => void
  businessContentPerPage: number
}

export const defaultBusinessContextValue: BusinessContextType = {
  search: '',
  updateSearch: (search: string): void => {
    console.warn(
      `The BusinessContext.updateSearch was called with value ${search}. Did you forget to use a BusinessProvider?`
    )
  },
  role: Roles.AllRoles,
  updateRole: (role: Roles): void => {
    console.warn(
      `The BusinessContext.updateRole was called with value ${role}. Did you forget to use a BusinessProvider?`
    )
  },
  type: FilterFileType.AllTypes,
  updateType: (type: FilterFileType): void => {
    console.warn(
      `The BusinessContext.updateType was called with value ${type}. Did you forget to use a BusinessProvider?`
    )
  },
  category: Categories.AllCategories,
  updateCategory: (category: Categories): void => {
    console.warn(
      `The BusinessContext.updateCategory was called with value ${category}. Did you forget to use a BusinessProvider?`
    )
  },
  resetContextToDefaults: (): void => {
    console.warn(
      `The BusinessContext.resetContextToDefaults was called by default. Did you forget to use a BusinessProvider?`
    )
  },
  businessContentPerPage: ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW,
  resetPage: (): void => {
    console.warn(
      `The BusinessContext.resetPage was called by default. Did you forget to use a BusinessProvider?`
    )
  },
  updatePage: (): void => {
    console.warn(
      `The BusinessContext.updatePage was called by default. Did you forget to use a BusinessProvider?`
    )
  },
}

export const BusinessContext = React.createContext<BusinessContextType>(
  defaultBusinessContextValue
)

export const useBusinessContext = (): typeof defaultBusinessContextValue =>
  React.useContext(BusinessContext)

export type TestBusinessContextConfig = Partial<
  typeof defaultBusinessContextValue
>

interface BusinessProviderProps extends PropsWithChildren {
  /** Useful when needing to override and spy on values or setters in testing. */
  testConfig?: TestBusinessContextConfig
}

const BusinessProvider: React.FC<BusinessProviderProps> = ({
  testConfig,
  children,
}) => {
  const [search, setSearch] = useState(defaultBusinessContextValue.search)
  const [type, setType] = useState(defaultBusinessContextValue.type)
  const [role, setRole] = useState(defaultBusinessContextValue.role)
  const [category, setCategory] = useState(defaultBusinessContextValue.category)
  const [businessContentPerPage, setBusinessContentPerPage] = useState(
    ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW
  )

  const updateSearch = (newSearch: string) => {
    setSearch(newSearch)
  }

  const updateRole = (newRole: Roles) => {
    setRole(newRole)
  }

  const updateType = (newType: FilterFileType) => {
    setType(newType)
  }

  const updateCategory = (newCategory: Categories) => {
    setCategory(newCategory)
  }

  const resetPage = () => {
    setBusinessContentPerPage(ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW)
  }

  const resetContextToDefaults = () => {
    updateType(defaultBusinessContextValue.type)
    updateSearch(defaultBusinessContextValue.search)
    updateCategory(defaultBusinessContextValue.category)
    updateRole(defaultBusinessContextValue.role)
    resetPage()
  }

  const updatePage = () => {
    setBusinessContentPerPage(
      businessContentPerPage + ADDITIONAL_MAXIMUM_AMOUNT_OF_CARDS_TO_SHOW
    )
  }

  const value: BusinessContextType = {
    search,
    type,
    role,
    category,
    updateSearch,
    updateRole,
    updateType,
    updateCategory,
    resetContextToDefaults,
    ...testConfig,
    updatePage,
    resetPage,
    businessContentPerPage,
  }

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  )
}

export default BusinessProvider
